import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  LinearProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Button,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as RunningIcon,
  ContentCopy as CopyIcon,
  Replay as ReplayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { executionApi } from '../../services/api';
import { AISystemExecution, ExpertExecutionDetail as ExpertExecutionDetailType } from '../../types';
import { PageHeader, LoadingSpinner, ErrorAlert } from '../common';
import PageBackButton from '../../../components/PageBackButton';
import FullscreenTextArea from './FullscreenTextArea';
import { formatLoopItem, getLoopIterationLabel, getLoopMetadata } from './runsPresentation';
import {
  evaluatorModelSnapshot,
  formatModelPricing,
  getModelDisplayName,
} from '../../utils/modelPricing';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  isMobile?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, isMobile, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`execution-tabpanel-${index}`}
      aria-labelledby={`execution-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: isMobile ? 1 : 2 }}>{children}</Box>}
    </div>
  );
}

const ExecutionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const [tabValue, setTabValue] = useState(0);
  const [expandedExpert, setExpandedExpert] = useState<string | false>(false);
  const [rerunningExpert, setRerunningExpert] = useState<number | null>(null);
  const [retryingExecution, setRetryingExecution] = useState(false);
  const [stoppingExecution, setStoppingExecution] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch execution data
  const {
    data: execution,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['execution-detail', id],
    queryFn: () => executionApi.get(id!),
    enabled: !!id,
  });

  // Auto-refresh running executions every 5s
  useEffect(() => {
    if (!execution) return;
    const isRunning = execution.status === 'RUNNING' || execution.status === 'running' || execution.status === 'start';
    if (!isRunning) return;
    const interval = setInterval(() => refetch(), 5000);
    return () => clearInterval(interval);
  }, [execution?.status, refetch]);

  if (isLoading) {
    return <LoadingSpinner message="Loading execution details..." />;
  }

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  if (!execution) {
    return (
      <Box>
        <PageHeader
          title="Execution Not Found"
          backButton={<PageBackButton label="Back to Runs" to="/ai/analytics?tab=runs" preferHistory={false} hideLabelOnMobile />}
        />
        <Alert severity="warning">Execution with ID {id} was not found.</Alert>
      </Box>
    );
  }

  // --- Handlers ---
  const handleRerunExpert = async (expertId: number, expertName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setRerunningExpert(expertId);
    try {
      const response = await executionApi.rerunExpert(execution.id, expertId, {
        skip_cache: true,
        use_original_context: true,
      });
      if (response.success) {
        setSnackbar({ open: true, message: `Expert "${expertName}" rerun successfully`, severity: 'success' });
        refetch();
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to rerun expert', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || 'Failed to rerun expert', severity: 'error' });
    } finally {
      setRerunningExpert(null);
    }
  };

  const handleRetryExecution = async () => {
    setRetryingExecution(true);
    try {
      const response = await executionApi.retryExecution(execution.id, { skip_cache: true });
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || `Execution retried successfully. ${response.retried_experts || 0} expert(s) re-run.`,
          severity: 'success',
        });
        refetch();
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to retry execution', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || 'Failed to retry execution', severity: 'error' });
    } finally {
      setRetryingExecution(false);
    }
  };

  const handleStopExecution = async () => {
    setStoppingExecution(true);
    try {
      const response = await executionApi.stopExecution(execution.id);
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Execution stopped successfully', severity: 'success' });
        refetch();
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to stop execution', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || 'Failed to stop execution', severity: 'error' });
    } finally {
      setStoppingExecution(false);
    }
  };

  // --- Helpers ---
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': case 'SUCCESS': case 'completed':
        return <SuccessIcon color="success" />;
      case 'ERROR': case 'FAILED': case 'error':
        return <ErrorIcon color="error" />;
      case 'RUNNING': case 'running': case 'start':
        return <RunningIcon color="primary" />;
      default:
        return <RunningIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': case 'SUCCESS': case 'completed': return 'success';
      case 'ERROR': case 'FAILED': case 'error': return 'error';
      case 'RUNNING': case 'running': case 'start': return 'primary';
      case 'stopped': return 'warning';
      default: return 'default';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' });
  };

  const formatJSON = (obj: any) => {
    try {
      if (!obj) return String(obj);
      const displayObj = { ...obj };
      if (displayObj.files && Array.isArray(displayObj.files)) {
        displayObj.files = `[${displayObj.files.length} files displayed separately]`;
      }
      return JSON.stringify(displayObj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const calculateExpertDuration = (result: ExpertExecutionDetailType, index: number): string => {
    if (result.metadata?.duration) return `${result.metadata.duration.toFixed(2)}s`;
    if (index === 0) {
      const start = new Date(execution.created_at).getTime();
      const end = new Date(result.created_at).getTime();
      return `${((end - start) / 1000).toFixed(2)}s`;
    } else {
      const prevResult = execution.expert_results[index - 1];
      const start = new Date(prevResult.created_at).getTime();
      const end = new Date(result.created_at).getTime();
      return `${((end - start) / 1000).toFixed(2)}s`;
    }
  };

  const formatMemory = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const formatDuration = (ms: number): string => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms.toFixed(0)}ms`;
  };

  const renderFiles = (metadata: any) => {
    if (!metadata?.files || !Array.isArray(metadata.files) || metadata.files.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Sent Files ({metadata.files.length})</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {metadata.files.map((file: any, index: number) => (
            <Paper key={index} variant="outlined" sx={{ p: 1, maxWidth: 300 }}>
              <Typography variant="caption" display="block" gutterBottom title={file.filename}>
                {file.filename || `File ${index + 1}`}
              </Typography>
              {file.mime_type?.startsWith('image/') && file.base64 ? (
                <img src={`data:${file.mime_type};base64,${file.base64}`} alt={file.filename}
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }} />
              ) : (
                <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.100', textAlign: 'center', minWidth: 150 }}>
                  <Typography variant="body2">{file.mime_type || 'Unknown type'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round((file.base64?.length || 0) / 1024)} KB
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  // --- Computed values ---
  const totalCost = execution.total_cost ?? execution.expert_results.reduce((sum, r) => sum + (r.total_cost || 0), 0);
  const totalTokens = execution.total_tokens ?? execution.expert_results.reduce((sum, r) => sum + (r.total_token || 0), 0);
  const computeCost = execution.compute_cost ?? 0;
  const expertsCost = execution.experts_cost ?? (totalCost - computeCost);
  const executionTimeMs = execution.execution_time_ms;
  const cpuTimeMs = execution.cpu_time_ms;
  const peakMemoryBytes = execution.peak_memory_bytes;
  const completedExperts = execution.expert_results.filter((r) => {
    const s = r.status?.toUpperCase();
    return s === 'SUCCESS' || s === 'CACHED' || s === 'COMPLETED';
  }).length;
  const totalExperts = execution.metadata?.total_experts || execution.expert_results.length;

  const isRunning = execution.status === 'RUNNING' || execution.status === 'running' || execution.status === 'start';
  const isErrorOrStopped = execution.status === 'error' || execution.status === 'ERROR' || execution.status === 'FAILED' ||
    execution.status === 'stopped' || execution.status === 'completed' || execution.status === 'COMPLETED' ||
    execution.status === 'SUCCESS';

  return (
    <Box>
      <PageHeader
        title="Execution Details"
        backButton={<PageBackButton label="Back to Runs" to="/ai/analytics?tab=runs" preferHistory={false} hideLabelOnMobile />}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Stop button for running executions */}
            {isRunning && (
              <Button
                onClick={handleStopExecution}
                disabled={stoppingExecution}
                color="error"
                variant="outlined"
                size="small"
                startIcon={stoppingExecution ? <CircularProgress size={16} /> : <StopIcon />}
              >
                {stoppingExecution ? 'Stopping...' : 'Stop'}
              </Button>
            )}
            {/* Retry/Re-run button */}
            {isErrorOrStopped && !isRunning && (
              <Button
                onClick={handleRetryExecution}
                disabled={retryingExecution}
                color={execution.status === 'error' || execution.status === 'stopped' ? 'warning' : 'primary'}
                variant="outlined"
                size="small"
                startIcon={retryingExecution ? <CircularProgress size={16} /> : <ReplayIcon />}
              >
                {retryingExecution ? 'Retrying...' : (
                  execution.status === 'error' ? 'Retry Failed' :
                  execution.status === 'stopped' ? 'Resume' : 'Re-run'
                )}
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} color="primary" size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* System & ID info */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {execution.system_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">•</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {execution.id}
        </Typography>
        <Tooltip title="Copy ID">
          <IconButton size="small" onClick={() => copyToClipboard(String(execution.id))}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Section */}
      <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: 'background.default' }}>
        <Grid container spacing={isMobile ? 1.5 : 2}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              {getStatusIcon(execution.status)}
              <Chip label={execution.status_display} color={getStatusColor(execution.status) as any} size="small" />
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Execution Type</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>{execution.execution_type_display}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Experts Completed</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>{completedExperts} / {totalExperts}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Experts Cost</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>
              ${expertsCost.toFixed(4)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({totalTokens.toLocaleString()} tokens)
              </Typography>
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Compute Cost</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>${computeCost.toFixed(6)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Total Cost</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5} color="primary">${totalCost.toFixed(4)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Started At</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>{new Date(execution.created_at).toLocaleString()}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Completed At</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>
              {execution.completed_at ? new Date(execution.completed_at).toLocaleString() : 'In Progress'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="body2" fontWeight="medium" mt={0.5}>
              {executionTimeMs != null
                ? formatDuration(executionTimeMs)
                : execution.completed_at
                  ? `${((new Date(execution.completed_at).getTime() - new Date(execution.created_at).getTime()) / 1000).toFixed(2)}s`
                  : 'Running...'}
            </Typography>
          </Grid>
          {cpuTimeMs != null && (
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">CPU Time</Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>{formatDuration(cpuTimeMs)}</Typography>
            </Grid>
          )}
          {peakMemoryBytes != null && peakMemoryBytes > 0 && (
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Peak Memory</Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>{formatMemory(peakMemoryBytes)}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_e, v) => setTabValue(v)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              '& .MuiTab-root': {
                minWidth: isMobile ? 'auto' : 90,
                px: isMobile ? 1.5 : 2,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                borderBottom: '2px solid transparent',
                '&.Mui-selected': {
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                },
              },
            }}
          >
            <Tab label={isMobile ? 'Results' : 'Expert Results'} />
            <Tab label={isMobile ? 'Actions' : 'Actions Summary'} />
            <Tab label={isMobile ? 'Logs' : 'System Logs'} />
            <Tab label={isMobile ? 'Meta' : 'Result & Metadata'} />
          </Tabs>
        </Box>

        {/* Expert Results Tab */}
        <TabPanel value={tabValue} index={0} isMobile={isMobile}>
          {execution.expert_results.length === 0 ? (
            <Alert severity="info">No expert results yet</Alert>
          ) : (
            execution.expert_results.map((result, index) => {
              const loopMetadata = getLoopMetadata(result);
              const loopIterationLabel = getLoopIterationLabel(result);

              return (
              <Accordion
                key={result.id}
                expanded={expandedExpert === result.id}
                onChange={(_e, isExpanded) => setExpandedExpert(isExpanded ? result.id : false)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    display="flex"
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    flexDirection={isMobile ? 'column' : 'row'}
                    gap={isMobile ? 1 : 2}
                    width="100%"
                  >
                    <Box display="flex" alignItems="center" gap={1} width={isMobile ? '100%' : 'auto'}>
                      <Typography sx={{ flexShrink: 0 }} fontWeight="medium">#{index + 1}</Typography>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography fontWeight="medium" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {result.expert_name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Tooltip title={formatModelPricing(result)} arrow>
                            <Typography variant="caption" color="text.secondary">
                              {result.expert_type} • {getModelDisplayName(result, 'No model')}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ ml: isMobile ? 0 : 'auto' }}>
                      {loopIterationLabel && (
                        <Chip
                          label={loopIterationLabel}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: '22px', fontSize: '0.7rem', '& .MuiChip-label': { px: 1 } }}
                        />
                      )}
                      {/* Calls Summary Badge */}
                      {result.calls_summary && result.calls_summary.total > 0 && (
                        <Tooltip title={`${result.calls_summary.successful} successful, ${result.calls_summary.failed} failed`}>
                          <Chip
                            label={
                              result.calls_summary.failed > 0
                                ? `${result.calls_summary.successful}/${result.calls_summary.total} ${isMobile ? '' : (result.calls_summary.type === 'actions' ? 'actions' : 'calls')}`
                                : `${result.calls_summary.total} ${isMobile ? '' : (result.calls_summary.type === 'actions' ? 'actions' : 'calls')}`
                            }
                            size="small"
                            color={result.calls_summary.failed > 0 ? 'warning' : 'info'}
                            variant="outlined"
                            sx={{ height: '22px', fontSize: '0.7rem', '& .MuiChip-label': { px: 1 } }}
                            icon={result.calls_summary.failed > 0
                              ? <ErrorIcon sx={{ fontSize: '14px !important' }} />
                              : <SuccessIcon sx={{ fontSize: '14px !important' }} />}
                          />
                        </Tooltip>
                      )}
                      {/* Evaluator Badge */}
                      {result.evaluator_status === 'success' && result.evaluator_result && (
                        <Tooltip title={`Accuracy: ${result.evaluator_result.accuracy_percent ?? 'N/A'}% | Hallucination: ${result.evaluator_result.hallucination_percent ?? 'N/A'}%`}>
                          <Chip
                            label={`${result.evaluator_result.accuracy_percent ?? '?'}%`}
                            size="small"
                            color={
                              (result.evaluator_result.accuracy_percent ?? 0) >= 70 &&
                              (result.evaluator_result.hallucination_percent ?? 100) <= 30
                                ? 'success'
                                : (result.evaluator_result.accuracy_percent ?? 0) >= 50
                                  ? 'warning' : 'error'
                            }
                            variant="outlined"
                            sx={{ height: '22px', fontSize: '0.7rem', '& .MuiChip-label': { px: 1 } }}
                          />
                        </Tooltip>
                      )}
                      <Chip label={result.status} color={getStatusColor(result.status) as any} size="small" />
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {calculateExpertDuration(result, index)}
                        </Typography>
                      )}
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'right' }}>
                          ${((result.total_cost || 0) - (result.compute_cost || 0)).toFixed(4)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Expert detail content */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Cost</Typography>
                      <Typography variant="body2">${(result.total_cost || 0).toFixed(4)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Tokens</Typography>
                      <Typography variant="body2">{(result.total_token || 0).toLocaleString()}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Model Snapshot</Typography>
                      <Typography variant="body2">{getModelDisplayName(result, 'No model')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Input / Output Price</Typography>
                      <Typography variant="body2">{formatModelPricing(result)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="body2">{calculateExpertDuration(result, index)}</Typography>
                    </Grid>
                    {loopMetadata && (
                      <>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">Loop Iteration</Typography>
                          <Typography variant="body2">{loopIterationLabel || '-'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary">Loop Source</Typography>
                          <Typography variant="body2">{loopMetadata.source || 'Not recorded'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary">Loop Item</Typography>
                          <Tooltip title={formatLoopItem(loopMetadata.item, 500)}>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              {loopMetadata.item_alias ? `${loopMetadata.item_alias}: ` : ''}
                              {formatLoopItem(loopMetadata.item)}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </>
                    )}
                    {result.compute_cost != null && result.compute_cost > 0 && (
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Compute Cost</Typography>
                        <Typography variant="body2">${result.compute_cost.toFixed(6)}</Typography>
                      </Grid>
                    )}
                    {result.cache_key && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">Cache Key</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {result.cache_key}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Evaluator Results */}
                  {result.evaluator_status === 'success' && result.evaluator_result && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Evaluator Results</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="caption" color="text.secondary">Model</Typography>
                            <Typography variant="body2">{getModelDisplayName(evaluatorModelSnapshot(result), 'N/A')}</Typography>
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="caption" color="text.secondary">Accuracy</Typography>
                            <Typography variant="body2" color={
                              (result.evaluator_result.accuracy_percent ?? 0) >= 70 ? 'success.main' :
                              (result.evaluator_result.accuracy_percent ?? 0) >= 50 ? 'warning.main' : 'error.main'
                            }>
                              {result.evaluator_result.accuracy_percent ?? 'N/A'}%
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="caption" color="text.secondary">Hallucination</Typography>
                            <Typography variant="body2" color={
                              (result.evaluator_result.hallucination_percent ?? 100) <= 30 ? 'success.main' :
                              (result.evaluator_result.hallucination_percent ?? 100) <= 50 ? 'warning.main' : 'error.main'
                            }>
                              {result.evaluator_result.hallucination_percent ?? 'N/A'}%
                            </Typography>
                          </Grid>
                          {result.evaluator_result.reasoning && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" color="text.secondary">Reasoning</Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{result.evaluator_result.reasoning}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {/* Files */}
                  {renderFiles(result.metadata)}

                  {/* Prompt */}
                  {result.prompt && (
                    <FullscreenTextArea
                      title={`${result.expert_name} - Prompt`}
                      content={result.prompt}
                      maxHeight="200px"
                      format="text"
                    />
                  )}

                  {/* Output */}
                  <Box sx={{ mt: 2 }}>
                    <FullscreenTextArea
                      title={`${result.expert_name} - Output`}
                      content={result.raw_output || '(empty)'}
                      maxHeight="300px"
                      format="text"
                    />
                  </Box>

                  {/* Parsed output if different from raw */}
                  {result.parsed_output && Object.keys(result.parsed_output).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <FullscreenTextArea
                        title={`${result.expert_name} - Parsed Output`}
                        content={formatJSON(result.parsed_output)}
                        maxHeight="200px"
                        format="json"
                      />
                    </Box>
                  )}

                  {/* Rerun button */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={rerunningExpert === result.expert_id ? <CircularProgress size={14} /> : <ReplayIcon />}
                      disabled={rerunningExpert === result.expert_id}
                      onClick={(e) => handleRerunExpert(result.expert_id, result.expert_name, e)}
                    >
                      {rerunningExpert === result.expert_id ? 'Rerunning...' : 'Rerun Expert'}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
              );
            })
          )}
        </TabPanel>

        {/* Actions Summary Tab */}
        <TabPanel value={tabValue} index={1} isMobile={isMobile}>
          {execution.actions_summary && execution.actions_summary.total > 0 ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h6">{execution.actions_summary.total}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Actions</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">{execution.actions_summary.successful}</Typography>
                    <Typography variant="caption" color="text.secondary">Successful</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h6" color="error.main">{execution.actions_summary.failed}</Typography>
                    <Typography variant="caption" color="text.secondary">Failed</Typography>
                  </Paper>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="text.secondary">Input / Output Price</Typography>
                            <Typography variant="body2">{formatModelPricing(evaluatorModelSnapshot(result))}</Typography>
                          </Grid>
                        </Grid>

              {execution.actions_summary.actions?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Action Details</Typography>
                  {execution.actions_summary.actions.map((action, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {action.status === 'success'
                          ? <SuccessIcon color="success" sx={{ fontSize: 18 }} />
                          : <ErrorIcon color="error" sx={{ fontSize: 18 }} />}
                        <Typography variant="body2" fontWeight="medium">{action.action_type}</Typography>
                        <Typography variant="caption" color="text.secondary">({action.expert_name})</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>{action.message}</Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Per-expert tool calls via metadata */}
              {execution.expert_results.map((result) => {
                const toolCalls = result.metadata?.tool_calls;
                if (!toolCalls || !Array.isArray(toolCalls) || toolCalls.length === 0) return null;
                return (
                  <Box key={result.id} sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {result.expert_name} - Tool Calls ({toolCalls.length})
                    </Typography>
                    {toolCalls.map((toolCall: any, tcIdx: number) => (
                      <Paper key={tcIdx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          {toolCall.status === 'success'
                            ? <SuccessIcon color="success" sx={{ fontSize: 16 }} />
                            : <ErrorIcon color="error" sx={{ fontSize: 16 }} />}
                          <Typography variant="body2" fontWeight="medium">{toolCall.tool_name || 'Unknown tool'}</Typography>
                        </Box>
                        {toolCall.tool_input && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>Input:</Typography>
                            <Box component="pre" sx={{ mt: 0.5, p: 1, bgcolor: isDark ? 'grey.800' : 'grey.100', borderRadius: 1, fontSize: '0.7rem', overflow: 'auto', maxHeight: '120px' }}>
                              {formatJSON(toolCall.tool_input)}
                            </Box>
                          </Box>
                        )}
                        {toolCall.tool_output && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>Output:</Typography>
                            <Box component="pre" sx={{ mt: 0.5, p: 1, bgcolor: isDark ? 'grey.800' : 'grey.100', borderRadius: 1, fontSize: '0.7rem', overflow: 'auto', maxHeight: '120px' }}>
                              {typeof toolCall.tool_output === 'string' ? toolCall.tool_output : formatJSON(toolCall.tool_output)}
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info">No actions or tool calls recorded for this execution.</Alert>
          )}
        </TabPanel>

        {/* System Logs Tab */}
        <TabPanel value={tabValue} index={2} isMobile={isMobile}>
          {/* System-level logs */}
          <FullscreenTextArea
            title="System Execution Logs"
            content={execution.logs || '(no system logs)'}
            maxHeight="300px"
            format="text"
          />

          {/* Per-expert logs */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Expert Logs</Typography>
          {execution.expert_results.map((result, idx) => (
            <Accordion key={idx} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  {getStatusIcon(result.status)}
                  <Typography fontWeight="medium">{result.expert_name}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FullscreenTextArea
                  title={`${result.expert_name} - Logs`}
                  content={result.logs || '(no logs)'}
                  maxHeight="200px"
                  format="text"
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </TabPanel>

        {/* Result & Metadata Tab */}
        <TabPanel value={tabValue} index={3} isMobile={isMobile}>
          {/* System result */}
          <FullscreenTextArea
            title="System Result"
            content={execution.result || '(no result)'}
            maxHeight="300px"
            format="text"
          />

          {/* System metadata */}
          {execution.metadata && Object.keys(execution.metadata).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <FullscreenTextArea
                title="System Execution Metadata"
                content={formatJSON(execution.metadata)}
                maxHeight="300px"
                format="json"
              />
            </Box>
          )}

          {/* Expert Results Metadata */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Expert Results Metadata</Typography>
          {execution.expert_results.map((result, idx) => (
            <Accordion key={idx} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">{result.expert_name} - Metadata</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FullscreenTextArea
                  title={`${result.expert_name} - Metadata`}
                  content={formatJSON(result.metadata)}
                  maxHeight="300px"
                  format="json"
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </TabPanel>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExecutionDetail;
